/**
 * DV-6834: the temporal variations saved on a product line, read from its attributes. Kept apart from
 * TemporalVariation.ts, with no imports, so BaseBOM can use it without pulling Variation or ArticleCollection.
 *
 * A temporal variation leaves `attributes.<attribute> = { code, bov: 'T', availability: { since, until } }` on the
 * saved product; `until` is the start of the day after the last valid one (the ERP validity is inclusive).
 */

export const TEMPORAL_BOV = 'T'

/** Saved product attribute names (BaseBOM) by variation type. */
export const ATTRIBUTE_BY_TYPE: Record<string, string> = {
    color: 'finish',
    temperature: 'ledColourTemp',
    control: 'control',
    installation: 'installation',
}

/** A temporal variation applied to a saved product (its persisted attribute). */
export interface SavedTemporalAttribute {
    attribute: string
    code: string
    since: number | null
    until: number | null
}

const isValidTimestamp = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value)

export const isTemporalBov = (bov: unknown) => bov === TEMPORAL_BOV

/** Every saved attribute of a product (BaseBOM `attributes`) that is a temporal variation. */
export const getSavedTemporalAttributes = (attributes: Record<string, any> | null | undefined): SavedTemporalAttribute[] => {
    if (!attributes || typeof attributes !== 'object') return []
    return Object.values(ATTRIBUTE_BY_TYPE)
        .filter((attribute) => isTemporalBov(attributes[attribute]?.bov))
        .map((attribute) => {
            const saved = attributes[attribute]
            let since: number | null = null
            let until: number | null = null
            if (isValidTimestamp(saved?.availability?.since)) since = saved.availability.since
            if (isValidTimestamp(saved?.availability?.until)) until = saved.availability.until
            return { attribute, code: String(saved?.code ?? ''), since, until }
        })
}

export const hasSavedTemporalAttributes = (attributes: Record<string, any> | null | undefined) =>
    getSavedTemporalAttributes(attributes).length > 0

/** Whether a saved temporal attribute is out of its validity window at `now` (inclusive last day). */
export const isSavedTemporalAttributeExpired = (saved: SavedTemporalAttribute, now: number) => {
    const startsLater = isValidTimestamp(saved.since) && now < saved.since
    const ended = isValidTimestamp(saved.until) && now >= saved.until
    return startsLater || ended
}
